"use client";

import Image, { StaticImageData } from "next/image";
import type { Session } from "next-auth";
import { MouseEventHandler, useMemo, useRef, useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";

import { cn } from "@/lib/utils";
import type { Notification, NotificationResponse } from "@/types";
import { useOutOfBoundsHitToggle, useWindowResizeSizing } from "@/lib/hooks";
import {
	useGetNotificationsQuery,
	useMarkAllNotificationsReadMutation,
	useUpdateNotificationMutation,
} from "@/features/notifications/notificationApiInjection";
import { ga4NotificationOpen } from "@/lib/analytics/ga4-ecommerce";
import { WEB_PUSH_PROMPT_EVENT } from "@/lib/firebase/web-push";

import { useToast } from "@/components/ui/use-toast";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import NotificationIcon from "@/components/icons/NotificationIcon";
import notificationTabIcon from "@/../public/assets/home/navigation/notification-tab.svg";
import notificationTabUnreadIcon from "@/../public/assets/home/navigation/notification-tab.svg";
import Icon from "@/components/ui/icon";

const NAV_DROPDOWN_OPEN_EVENT = "techxeed:nav-dropdown-open";
const NAV_DROPDOWN_ID = "notifications";

export default function NavNotificationButton() {
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [showDropdown, setDropdownVisibility] = useState(false);
	const [mounted, setMounted] = useState(false);
	const { data: session, status: sessionStatus } = useSession();

	useOutOfBoundsHitToggle(wrapperRef, [showDropdown, setDropdownVisibility]);

	// Only fetch notifications if user is signed in and has a valid session token
	const hasValidSession = session?.idToken && session.idToken.trim().length > 0;
	const { data, isLoading } = useGetNotificationsQuery(undefined, {
		skip: !hasValidSession || sessionStatus === "loading",
		refetchOnMountOrArgChange: true,
		refetchOnFocus: true,
		refetchOnReconnect: true,
		pollingInterval: 60_000,
	});
	const unreadNotifications = useMemo(() => data?.docs.filter((x) => !x.isRead) ?? [], [data]);
	const [markAllNotificationsRead] = useMarkAllNotificationsReadMutation();

	useEffect(() => {
		// Mark as mounted after hydration to prevent hydration mismatches
		setMounted(true);
	}, []);

	useEffect(() => {
		const closeForOtherDropdown = (event: Event) => {
			if ((event as CustomEvent<string>).detail !== NAV_DROPDOWN_ID) {
				setDropdownVisibility(false);
			}
		};

		window.addEventListener(NAV_DROPDOWN_OPEN_EVENT, closeForOtherDropdown);
		return () => window.removeEventListener(NAV_DROPDOWN_OPEN_EVENT, closeForOtherDropdown);
	}, []);

	useEffect(() => {
		if (!showDropdown || unreadNotifications.length === 0) return;
		void markAllNotificationsRead();
	}, [markAllNotificationsRead, showDropdown, unreadNotifications.length]);

	return (
		<div ref={wrapperRef} className="group relative">
			<button
				aria-haspopup="menu"
				aria-controls="notification-dropdown"
				aria-expanded={showDropdown}
				aria-label="Notifications"
				className="relative z-[70] flex min-h-[44px] min-w-[44px] items-center justify-center md:size-10"
				onClick={() => {
					const nextOpen = !showDropdown;
					setDropdownVisibility(nextOpen);
					if (nextOpen) {
						window.dispatchEvent(new CustomEvent(NAV_DROPDOWN_OPEN_EVENT, { detail: NAV_DROPDOWN_ID }));
						// Safari only allows Notification.requestPermission() in this click stack —
						// not via deferred custom-event handlers or timers.
						if (
							hasValidSession &&
							typeof Notification !== "undefined" &&
							Notification.permission === "default"
						) {
							void Notification.requestPermission().then((permission) => {
								if (permission === "granted") {
									window.dispatchEvent(new Event(WEB_PUSH_PROMPT_EVENT));
								}
							});
						} else if (
							hasValidSession &&
							typeof Notification !== "undefined" &&
							Notification.permission === "granted"
						) {
							window.dispatchEvent(new Event(WEB_PUSH_PROMPT_EVENT));
						}
					}
				}}
			>
				<NotificationIconComponent isOpened={showDropdown} />
			</button>
			
			{/* Only show badge after hydration to prevent hydration mismatches - positioned relative to parent div */}
			{mounted && unreadNotifications.length > 0 && (
				<div 
					id="notification-badge-count"
					className="pointer-events-none absolute right-0.5 top-0 z-[80] inline-flex size-3.5 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-md md:right-1 md:top-0.5 md:size-4 md:text-xxsm"
					style={{ 
						backgroundColor: 'rgb(63, 224, 208)',
						background: 'rgb(63, 224, 208)',
						opacity: 1,
					}}
				>
					<span style={{ color: '#FFFFFF', fontWeight: 'bold' }}>{unreadNotifications.length}</span>
				</div>
			)}
			{showDropdown && mounted && (
				<div
					id="notification-dropdown"
					className="fixed left-1/2 top-[calc(90px+1.5rem)] z-[60] w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 md:absolute md:left-auto md:right-0 md:top-5 md:w-auto md:max-w-none md:translate-x-0"
				>
					<div aria-hidden className="hidden md:block h-12" />
					<NotificationDropdown
						data={data}
						unreadNotifications={unreadNotifications}
						isLoading={isLoading}
					/>
				</div>
			)}
		</div>
	);
}

function NotificationIconComponent({ isOpened }: { isOpened: boolean }) {
	return (
		<NotificationIcon
			className={isOpened ? "size-[22px] text-foreground md:size-[26px]" : "size-5 text-foreground md:size-6"}
			aria-hidden
		/>
	);
}

type NotificationDropdownProps = {
	data?: NotificationResponse;
	unreadNotifications: Notification[];
	isLoading: boolean;
};
function NotificationDropdown({ data, unreadNotifications, isLoading }: NotificationDropdownProps) {
	const { toast } = useToast();
	const router = useRouter();
	const dropdownMaxHeight = useWindowResizeSizing(0, -200);
	const [updateNotification, { isLoading: isUpdating }] = useUpdateNotificationMutation();

	const baseClass = cn(
		"notification-dropdown-surface dropdown-glassmorphism flex min-h-32 flex-col overflow-hidden rounded-2xl transition-opacity duration-300",
		{ "items-center justify-center h-32": isLoading, "opacity-75": isUpdating },
	);

	const onNotificationClick = async (notification: Notification, readStatus = true) => {
		const id = notification._id;
		if (!id) return;

		const updated = await updateNotification({
			id,
			body: { isRead: readStatus },
		});

		if ("error" in updated) {
			toast({
				title: "Failed to update the seen status",
				description: updated.error.toString(),
				variant: "destructive",
			});
			return;
		}

		const path = getNotificationRedirectPath(notification.redirectPath);
		ga4NotificationOpen({ notification_id: id });
		if (path) router.push(path);
	};

	if (isLoading) {
		return (
			<div 
				className={cn(baseClass, "w-full sm:w-96")}
				style={{
					border: '1px solid var(--glass-border)',
					boxShadow: 'var(--glass-shadow)',
				}}
			>
				<LoadingSpinner />
			</div>
		);
	}

	return (
		<div
			className={cn(baseClass, "w-full sm:w-96")}
			style={{ 
				maxHeight: `calc(100vh - 90px - 1.5rem - 2rem)`,
				border: '1px solid var(--glass-border)',
				boxShadow: 'var(--glass-shadow)',
			}}
		>
			<Tabs
				defaultValue="all"
				className="flex min-h-0 flex-1 flex-col"
			>
				<TabsList className="notification-tabs-list mx-auto mt-4 h-8 shrink-0 text-foreground">
					<TabsTrigger 
						value="all" 
						className="notification-tab-trigger h-7 w-20 text-xs text-foreground data-[state=active]:bg-transparent"
					>
						<span className="relative z-10">All</span>
					</TabsTrigger>
					<TabsTrigger 
						value="unread" 
						className="notification-tab-trigger h-7 w-20 text-xs text-foreground data-[state=active]:bg-transparent"
					>
						<span className="relative z-10">Unread</span>
					</TabsTrigger>
				</TabsList>

				<TabsContent role="list" value="all" className="muted-scrollbar min-h-0 flex-1 overflow-y-auto">
					{data === undefined || data.docs.length === 0 ? (
						<NotificationEmptyRow />
					) : (
						data?.docs.map((x) => (
							<NotificationRow
								key={x._id ?? x.content}
								data={x}
								onClick={() => onNotificationClick(x)}
							/>
						))
					)}
				</TabsContent>

				<TabsContent role="list" value="unread" className="muted-scrollbar min-h-0 flex-1 overflow-y-auto">
					{unreadNotifications.length === 0 ? (
						<NotificationEmptyRow />
					) : (
						unreadNotifications.map((x) => (
							<NotificationRow
								key={x._id ?? x.content}
								data={x}
								onClick={() => onNotificationClick(x)}
							/>
						))
					)}
				</TabsContent>

				{/* <div className="flex h-10 min-h-10 flex-1 items-center justify-end border-t border-secondary/10 px-2">
					<Link href="/settings">
						<Image src={settingsIcon} alt="Settings" className="size-7" />
					</Link>
				</div> */}
			</Tabs>
		</div>
	);
}

type NotificationRowProps = {
	data: Notification;
	onClick: MouseEventHandler<HTMLButtonElement>;
};

function NotificationRow({ data, onClick }: NotificationRowProps) {
	// const icon = data.isRead ? notificationTabIcon : notificationTabUnreadIcon;

	return (
		<button
			role="listitem"
			onClick={onClick}
			data-unread={!data.isRead ? "true" : "false"}
			className={cn(
				"grid grid-cols-[3rem_1fr] w-full gap-4 p-4 font-light text-foreground transition-colors",
				"hover:bg-secondary/[0.04] hover:text-secondary",
				"notification-row"
			)}
			style={{
				minWidth: 0,
				...(!data.isRead
					? {
							backgroundColor: "rgba(63, 224, 208, 0.25)",
							background: "rgba(63, 224, 208, 0.25)",
						}
					: {
							backgroundColor: "transparent",
							background: "transparent",
						}),
			}}
		>
			<div
				className="notification-icon-circle flex size-12 items-center justify-center justify-self-center rounded-full shrink-0"
				aria-label={data.isRead ? "Read" : "Unread"}
				style={{
					backgroundColor: 'rgba(63, 224, 208, 0.4)',
					background: 'rgba(63, 224, 208, 0.4)',
					opacity: 1,
				}}
			>
				<Icon
					src={notificationTabUnreadIcon}
					alt={" "}
					aria-hidden
					size={24}
					className="w-6"
				/>
			</div>
			<div className="min-w-0 flex flex-col">
				<span className="text-start text-xsm2 font-light leading-tight break-words overflow-wrap-anywhere">
				{data.content}
			</span>
			</div>
		</button>
	);
}

function getNotificationRedirectPath(redirectPath?: string | null) {
	const path = redirectPath?.trim();
	if (!path || !path.startsWith("/") || path.startsWith("//")) return null;
	if (path === "/chats") return "/profile/inbox";
	return path;
}

function NotificationEmptyRow() {
	return <span className="mx-auto block py-4 text-center">You are all caught up</span>;
}
